import React from "react";

import {ColumnDef} from "../../ColumnDef";
import {NUMBER} from "../../../constants";
import {Cell} from "../../Cell";
import {Button, Tooltip} from "@mui/material";
import {isEmpty} from "weed-js";
import {Row} from "../../Row";

import {Count} from "./operations/Count.js";
import {CountDistinct} from "./operations/CountDistinct.js";
import {Max} from "./operations/Max.js";
import {Mean} from "./operations/Mean.js";
import {Median} from "./operations/Median.js";
import {Min} from "./operations/Min.js";
import {Percentile} from "./operations/Percentile.js";
import {Stddev} from "./operations/Stddev.js";
import {Sum} from "./operations/Sum.js";
import {Variance} from "./operations/Variance.js";
import {CheckCircle, Pending} from "@mui/icons-material";


export class Aggregation extends ColumnDef {

    static operations = {
        Count: Count,
        CountDistinct: CountDistinct,
        Max: Max,
        Mean: Mean,
        Median: Median,
        Min: Min,
        Percentile: Percentile,
        Stddev: Stddev,
        Sum: Sum,
        Variance: Variance
    }

    constructor({
        column,
        operation,
        index = null
    }) {
        super({
            displayName: `${operation.constructor.displayName()}(${column.displayName})`,
            options: {...column.options},
            index: index, // Must be set later.
            type: operation.constructor.typeOverride() || column.type || NUMBER,
            style: {...column.style},
        });

        this.column = column; // The column that is being aggregated
        this.operation = operation;
        this.groups = new Map();
        this.orderedGroupKeys = [];
        this.observedGroupKeys = new Set();
        this.isStateChange = true; // This is a state change that will be applied to the view
    }

    toJSON() {
        return {
            ...super.toJSON(),
            operation: this.operation.constructor.name,
            className: this.constructor.name,
            index: this.getIndex(),
            column: this.column.toJSON(),
            id: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15), // Unique ID for this instance
        }
    }

    static fromJSON(json) {
        const column = ColumnDef.fromJSON(json.column);
        const aggregation = new Aggregation({
            column: column,
            operation: new Aggregation.operations[json.operation]({column}),
            index: json.index,
        });
        return aggregation;
    }

    rollback() {
        super.rollback();
        this.groups = new Map();
        this.orderedGroupKeys = [];
        this.observedGroupKeys = new Set();
    }

    static getNumericOperations() {
        /**
         * This method returns an object with the names of the operations that can be performed on numeric columns.
         * The keys are the names of the operations, and the values are the operation classes.
         */
        const numericOperations = {};
        for (const [name, operation] of Object.entries(Aggregation.operations)) {
            if (operation.isNumeric()) {
                numericOperations[name] = operation;
            }
        }
        return numericOperations;
    }

    static getCategoricalOperations() {
        /**
         * This method returns an object with the names of the operations that can be performed on categorical columns.
         * The keys are the names of the operations, and the values are the operation classes.
         */
        const categoricalOperations = {};
        for (const [name, operation] of Object.entries(Aggregation.operations)) {
            if (operation.isCategorical()) {
                categoricalOperations[name] = operation;
            }
        }
        return categoricalOperations;
    }

    static getDateOperations() {
        /**
         * This method returns an object with the names of the operations that can be performed on date columns.
         * The keys are the names of the operations, and the values are the operation classes.
         */
        const dateOperations = {};
        for (const [name, operation] of Object.entries(Aggregation.operations)) {
            if (operation.handlesDate()) {
                dateOperations[name] = operation;
            }
        }
        return dateOperations;
    }

    getCell(groupKey, rowNumber) {
        const group = this.groups.get(groupKey);
        const rawValue = group.finish(this.operation);
        return new Cell(this, rawValue, rowNumber);
    }

    incrementGroup(groupKey, row) {
        if (!this.groups.has(groupKey)) {
            this.groups.set(groupKey, new this.operation.constructor({column: this.column}));
        }
        const group = this.groups.get(groupKey);
        if (!this.observedGroupKeys.has(groupKey)) {
            this.observedGroupKeys.add(groupKey);
            this.orderedGroupKeys.push(groupKey);
        }
        group.aggregateRow(row);
        this.operation.aggregateRow(row);
    }

    static groupKeyFromRow({groupBy, row}) {
        // This method is used to generate a unique key for the group based on the row.
        // It should return a string that uniquely identifies the group.
        if (!groupBy || groupBy.length === 0) {
            return "";
        }
        const asList = [];
        for (let i = 0; i < groupBy.length; i++) {
            asList.push(row.cells[groupBy[i].getIndex()].getSortValue());
        }
        return JSON.stringify(asList);
    }

    addToRow(groupKey, row) {
        /**
         * This method is used to add the aggregation to the aggregated row. The row is expected to start with values
         * corresponding to the group by columns, in order.
         */
        if (this.getIndex() === null) {
            this.setIndex(row.cells.length); // Set the index to the current length of cells in the row
        }
        if (row.cells[this.getIndex()] !== undefined) {
            return row;
        }
        row.cells.push(this.getCell(groupKey, row.rowNumber));
        return row;
    }

    key() {
        return `aggregation-${this.displayName}-${this.column.getIndex()}-${this.operation.name}`;
    }

    stage(view) {
        /**
         * Aggregations are applied in rounds. You can do several aggregations at once, each one should return a column
         * of cells to be added to the rows by their group by key, which is a JSON string of the values of the
         * group by columns.
         *
         * After all aggregations are run; the columns will be added to the existing rows, at which time the group-by
         * should be applied to the rows.
         */
        const {rows} = view.getState();
        const groupBy = view.getPendingGroupBys();

        if (isEmpty(groupBy) || isEmpty(rows)) {
            return view.getState();
        }

        const rowsByGroupKey = new Map();
        for (const row of rows) {
            const groupKey = Aggregation.groupKeyFromRow({groupBy, row});
            this.incrementGroup(groupKey, row);
            if (!rowsByGroupKey.has(groupKey)) {
                rowsByGroupKey.set(groupKey, []);
            }
            rowsByGroupKey.get(groupKey).push(row);
        }
        for (const groupKey of this.orderedGroupKeys) {
            if (!rowsByGroupKey.has(groupKey) || rowsByGroupKey.get(groupKey).length === 0) {
                console.warn("No rows found for group key:", groupKey);
                continue;
            }
            rowsByGroupKey.get(groupKey).forEach(row => {
                this.addToRow(groupKey, row);
            })
        }

        return view.getState();
    }

    renderPending(view) {
        return <div
            role={"active-view-option-row"}
            className="active-view-option-row"
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role={"pending-aggregation"}
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"Aggregating on this column has not yet been applied. Click \"Update View\" to apply."}>
                    <Pending className={"canold-table-pending-icon"}/>
                </Tooltip>
                {this.displayName}
            </span>
            <Button
                variant="outlined"
                className="pending-aggregation-remove-button"
                role="pending-aggregation-remove-button"
                onClick={() => {
                    view.removePendingStateChange(this)}
                }
            >
                Remove
            </Button>
        </div>
    }

    renderApplied(view) {
        return <div
            role="active-view-option-row"
            className="active-view-option-row"
            key={this.key()}>
            <span
                className="active-view-option-row-label"
                role={"applied-aggregation"}
            >
                <Tooltip slotProps={{
                    popper: { // Render the tooltip on top of everything else:
                        className: "canold-table-tooltip"
                    }
                }} title={"This aggregation has been applied. Click Remove or Reset Table to un-apply."}>
                    <CheckCircle className={"canold-table-applied-icon"}/>
                </Tooltip>
                {this.displayName}
            </span>
            <Button
                variant="outlined"
                className="applied-aggregation-remove-button"
                role="applied-aggregation-remove-button"
                onClick={() => {
                    view.removeAppliedStateChange(this)}
                }
            >
                Remove
            </Button>
        </div>
    }
}


export default Aggregation;
